import React, { createContext, useContext, useState, useCallback } from 'react';

const QuoteCartContext = createContext(null);

/**
 * Estrutura de um item no carrinho:
 * {
 *   itemKey: string (productId ou productId-var-variationId)
 *   productId: string
 *   variationId: string | null
 *   isVariation: boolean
 *   name: string (nome completo para exibição)
 *   productName: string (nome do produto principal)
 *   variationName: string | null
 *   brand: string
 *   image: string | null
 *   size: string | null
 *   quantity: number
 * }
 */

export const QuoteCartProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  // Gerar chave única para item
  const getItemKey = useCallback((productId, variationId = null) => {
    return variationId ? `${productId}-var-${variationId}` : productId;
  }, []);

  // Verificar se item está no carrinho
  const isInCart = useCallback((productId, variationId = null) => {
    const itemKey = getItemKey(productId, variationId);
    return items.some(item => item.itemKey === itemKey);
  }, [items, getItemKey]);

  // Obter item do carrinho
  const getCartItem = useCallback((productId, variationId = null) => {
    const itemKey = getItemKey(productId, variationId);
    return items.find(item => item.itemKey === itemKey);
  }, [items, getItemKey]);

  // Adicionar produto simples
  const addProduct = useCallback((product, quantity = 1) => {
    const itemKey = getItemKey(product.id);
    
    setItems(prev => {
      const existing = prev.find(item => item.itemKey === itemKey);
      if (existing) {
        // Atualizar quantidade
        return prev.map(item => 
          item.itemKey === itemKey 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...prev, {
        itemKey,
        productId: product.id,
        variationId: null,
        isVariation: false,
        name: product.name,
        productName: product.name,
        variationName: null,
        brand: product.brand,
        image: product.images?.[0] || null,
        size: product.size || null,
        quantity
      }];
    });
    
    return true;
  }, [getItemKey]);

  // Adicionar variação específica
  const addVariation = useCallback((product, variation, quantity = 1) => {
    const itemKey = getItemKey(product.id, variation.id);
    
    setItems(prev => {
      const existing = prev.find(item => item.itemKey === itemKey);
      if (existing) {
        // Atualizar quantidade
        return prev.map(item => 
          item.itemKey === itemKey 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...prev, {
        itemKey,
        productId: product.id,
        variationId: variation.id,
        isVariation: true,
        name: `${product.name} - ${variation.name}`,
        productName: product.name,
        variationName: variation.name,
        brand: product.brand,
        image: variation.image || product.images?.[0] || null,
        size: variation.size || product.size || null,
        quantity
      }];
    });
    
    return true;
  }, [getItemKey]);

  // Remover item
  const removeItem = useCallback((itemKey) => {
    setItems(prev => prev.filter(item => item.itemKey !== itemKey));
  }, []);

  // Remover por produto/variação
  const removeByProductVariation = useCallback((productId, variationId = null) => {
    const itemKey = getItemKey(productId, variationId);
    removeItem(itemKey);
  }, [getItemKey, removeItem]);

  // Atualizar quantidade
  const updateQuantity = useCallback((itemKey, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemKey);
      return;
    }
    
    setItems(prev =>
      prev.map(item =>
        item.itemKey === itemKey
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  }, [removeItem]);

  // Incrementar/Decrementar
  const incrementQuantity = useCallback((itemKey, delta = 1) => {
    setItems(prev =>
      prev.map(item => {
        if (item.itemKey === itemKey) {
          const newQty = Math.max(1, item.quantity + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  }, []);

  // Limpar carrinho
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Total de itens
  const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
  const totalUniqueItems = items.length;

  const value = {
    items,
    totalItems,
    totalUniqueItems,
    getItemKey,
    isInCart,
    getCartItem,
    addProduct,
    addVariation,
    removeItem,
    removeByProductVariation,
    updateQuantity,
    incrementQuantity,
    clearCart
  };

  return (
    <QuoteCartContext.Provider value={value}>
      {children}
    </QuoteCartContext.Provider>
  );
};

export const useQuoteCart = () => {
  const context = useContext(QuoteCartContext);
  if (!context) {
    throw new Error('useQuoteCart deve ser usado dentro de QuoteCartProvider');
  }
  return context;
};
